import React, { forwardRef, memo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { Box, Typography } from '@mui/material';
import moment from 'moment';

import { FORTA_BLUE } from '../../../utils/colors';
import { calculateTrialAverage, getChartLabelByDCM, getDataKeyByDCM } from '../../../pages/utils';
import { linearRegression } from '../../../helpers/utility-functions';
import {
  DurationEventMeasurementUnitEnum,
  GoalTypes,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TPGoalDataCollectionMethod,
} from './goals/common/constants/general';

import type {
  IDashboardTargetDay,
  IOption,
  IRecommendation,
  ITreatmentPlanGoal,
  ITrialSummary,
  ITrialSummaryTarget,
} from '../../../utils/types';
import type { FC } from 'react';

interface PdfDocumentProps {
  data: ITrialSummary[];
  client: IOption | null;
  treatmentPlan: IOption | null;
  goals: ITreatmentPlanGoal[];
  recommendation: IRecommendation | null;
}

const DURATION_UNIT_MAP = {
  [DurationEventMeasurementUnitEnum.hour]: 'hours',
  [DurationEventMeasurementUnitEnum.minute]: 'mins.',
  [DurationEventMeasurementUnitEnum.second]: 'secs.',
};

const TargetChart: FC<{ targetData: any }> = memo(function TargetChart({ targetData }) {
  const dcm = targetData.dcm;
  const dataKey = getDataKeyByDCM(dcm);

  const dataPoints = targetData.trials
    .map((trial: any) => ({
      x: moment(trial.date, 'MM/DD/YY').unix(),
      y: trial[dataKey],
    }))
    .filter((point: any) => !isNaN(point.y));

  if (dataPoints.length > 1) {
    const { slope, intercept } = linearRegression(dataPoints);

    const firstX = dataPoints[0].x;
    const lastX = dataPoints[dataPoints.length - 1].x;

    targetData.trials.forEach((trial: any) => {
      const x = moment(trial.date, 'MM/DD/YY').unix();
      if (x === firstX || x === lastX) {
        trial[`${dataKey}_trend`] = slope * x + intercept;
      }
    });
  }

  return (
    <ResponsiveContainer height={'99%'} width="99%" aspect={3}>
      <LineChart data={targetData?.trials} margin={{ top: 5, right: 10, left: 5, bottom: 30 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          label={{ value: 'Trial date', angle: 0, position: 'bottom', dy: 10 }}
          dy={10}
        />
        <YAxis
          label={{
            value: dcm === TPGoalDataCollectionMethod.PERCENTAGE ? '%' : getChartLabelByDCM(dcm),
            angle: dcm === TPGoalDataCollectionMethod.PERCENTAGE ? 0 : -90,
            position: 'insideLeft',
          }}
          tickFormatter={(value) => `${Math.round(value)}`}
          domain={dcm === TPGoalDataCollectionMethod.PERCENTAGE ? [0, 100] : [0, 'dataMax + 3']}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={FORTA_BLUE.primary}
          strokeWidth={2}
          activeDot={{ r: 3 }}
          isAnimationActive={false}
        />
        {dataPoints.length > 1 && (
          <Line
            connectNulls
            type="linear"
            dataKey={`${dataKey}_trend`}
            stroke={FORTA_BLUE.primary}
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
});

const PdfDocument = forwardRef<HTMLDivElement, PdfDocumentProps>(function PdfDocument(props, ref) {
  const { data, client, treatmentPlan, goals, recommendation } = props;

  const transformedData = React.useMemo(() => {
    if (!data) return [];

    return data
      .flat()
      .filter((goal) => goal && goal.targets)
      .flatMap((goal) =>
        goal.targets.map((target: ITrialSummaryTarget) => {
          const sortedTrials = [...target.trials].sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
          );

          let dcm = '';
          let rateUnit = '';
          let durationDCMEventUnit = '';

          const aggregatedTrials = sortedTrials.reduce((acc: any, trial: IDashboardTargetDay) => {
            const date = moment(trial.date).format('MM/DD/YY');
            if (!acc[date]) {
              acc[date] = {
                date,
                instances: 0,
                success: 0,
                attempts: 0,
                count: 0,
                duration: 0,
                rateTotal: 0,
                sessionDurationTotal: 0,
                rateTimeUnit: '',
                durationEventTotal: 0,
                durationEventCount: 0,
                durationTimeUnit: '',
              };
            }

            dcm = trial.data_collection_method ?? '';
            rateUnit = trial.measurement_units_interval_unit ?? '';
            durationDCMEventUnit = trial.measurement_units_duration_unit ?? '';

            acc[date].instances += trial.instances;
            acc[date].success += trial.success;
            acc[date].attempts += trial.attempts;
            acc[date].count += 1;

            acc[date].rateTotal += trial?.total_trial_rate_events ?? 0;
            acc[date].sessionDurationTotal += trial?.expected_duration ?? 0;
            acc[date].rateTimeUnit = trial?.measurement_units_interval_unit ?? '';
            acc[date].durationEventTotal +=
              trial?.duration_events?.reduce((sum, dur) => sum + dur, 0) ?? 0;
            acc[date].durationEventCount += trial?.duration_events?.length ?? 0;
            acc[date].durationTimeUnit = trial?.measurement_units_duration_unit ?? '';

            return { ...acc, [date]: { ...acc[date], durations: trial.durations } };
          }, {});

          const trials = Object.values(aggregatedTrials).map((trial: any) => {
            let sessionDurationConvertNum = 1;
            let durationUnitConvertNum = 1;
            let durationEventAverage = 0;
            let durationEventTotal = 0;
            if (trial.rateTimeUnit) {
              switch (trial.rateTimeUnit) {
                case RateMethodMeasurementUnitsIntervalUnitEnum.HOUR:
                  sessionDurationConvertNum = trial.sessionDurationTotal / 3600;
                  break;
                case RateMethodMeasurementUnitsIntervalUnitEnum.MINUTE:
                  sessionDurationConvertNum = trial.sessionDurationTotal / 60;
                  break;
                case RateMethodMeasurementUnitsIntervalUnitEnum.SECOND:
                  sessionDurationConvertNum = trial.sessionDurationTotal;
              }
            }
            const rate = parseFloat((trial.rateTotal / sessionDurationConvertNum).toFixed(2));

            if (trial.durationTimeUnit) {
              switch (trial.durationTimeUnit) {
                case DurationEventMeasurementUnitEnum.hour:
                  durationUnitConvertNum = 3600;
                  break;
                case DurationEventMeasurementUnitEnum.minute:
                  durationUnitConvertNum = 60;
                  break;
                case DurationEventMeasurementUnitEnum.second:
                  durationUnitConvertNum = 1;
              }
            }

            durationEventAverage = parseFloat(
              (
                trial.durationEventTotal /
                durationUnitConvertNum /
                trial.durationEventCount
              ).toFixed(2),
            );
            durationEventTotal = parseFloat(
              (trial.durationEventTotal / durationUnitConvertNum).toFixed(2),
            );
            return {
              date: trial.date,
              instances: trial.instances,
              success: trial.success,
              attempts: trial.attempts,
              durations: trial.durations,
              rate,
              type: dcm,
              durationEventAverage,
              durationEventTotal,
              percentage:
                trial.attempts > 0
                  ? parseFloat(((trial.success / trial.attempts) * 100).toFixed(2))
                  : 0,
            };
          });

          const goalType = goals.find((g) => g.id === goal.goal_id)?.goal_type || '';
          const averageResult = calculateTrialAverage(trials);

          return {
            goalId: goal.goal_id,
            goalLabel: goal.label,
            targetId: target.target_id,
            targetLabel: target.label,
            goalType,
            trials,
            dcm,
            rateUnit,
            durationDCMEventUnit,
            average: averageResult.value,
            averageLabel: averageResult.label,
            durationUnit: target.event_duration_measurement_unit,
          };
        }),
      );
  }, [data, goals]);

  if (!data || !client || !treatmentPlan || !goals || !recommendation) return null;

  const marginTop = '2cm';
  const marginRight = '0';
  const marginBottom = '2cm';
  const marginLeft = '0';
  const getPageMargins = () => {
    return `@page { 
    margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft} !important;
    @top-center {
      content: none;
    }
    @bottom-center {
      content: none;
    } }
    @page :first {
        margin-top: 0.5cm !important;
        margin-bottom: 0.5cm !important;
      }  
    `;
  };

  return (
    <Box ref={ref} sx={{ p: 4, height: 'fit-content' }}>
      <Typography variant="h3" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 2 }}>
        Treatment plan goals report
      </Typography>
      <style>{getPageMargins()}</style>
      <style>
        {`
          .page-break {
            page-break-after: always;
          }
        `}
      </style>
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Box sx={{ textAlign: 'left', mb: 2, width: '50%' }}>
          <Typography sx={{ mb: 1 }}>
            <span style={{ fontWeight: 700 }}>Client name:</span> {client?.label}
          </Typography>
          <Typography>
            <span style={{ fontWeight: 700 }}>Treatment plan:</span> {treatmentPlan?.label}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'left', mb: 2 }}>
          <Typography sx={{ mb: 1 }}>
            <span style={{ fontWeight: 700 }}>Authorization start date: </span>
            {recommendation?.date_start
              ? moment(recommendation?.date_start).format('MM/DD/YY')
              : 'N/A'}
          </Typography>
          <Typography>
            <span style={{ fontWeight: 700 }}>Authorization end date: </span>
            {recommendation?.date_start && recommendation?.duration
              ? moment(recommendation?.date_start)
                  .add(recommendation?.duration, 'months')
                  .format('MM/DD/YY')
              : 'N/A'}
          </Typography>
        </Box>
      </Box>
      {transformedData.map((targetData: any, index: number) => {
        const goal = goals.find((g) => g.id === targetData.goalId);
        const goalType = goal ? goal.goal_type : '';
        const dcm = targetData.dcm ?? '';
        const durationDCMEventUnit = targetData.durationDCMEventUnit;
        const rateUnit = targetData.rateUnit;
        let durationUnitLabel = '';
        switch (durationDCMEventUnit) {
          case DurationEventMeasurementUnitEnum.hour:
            durationUnitLabel = DURATION_UNIT_MAP[DurationEventMeasurementUnitEnum.hour];
            break;
          case DurationEventMeasurementUnitEnum.minute:
            durationUnitLabel = DURATION_UNIT_MAP[DurationEventMeasurementUnitEnum.minute];
            break;
          case DurationEventMeasurementUnitEnum.second:
            durationUnitLabel = DURATION_UNIT_MAP[DurationEventMeasurementUnitEnum.second];
            break;
        }
        return (
          <Box
            key={index}
            sx={{ marginBottom: 3, border: '1px solid #999', p: 3, borderRadius: 3 }}
            display={'flex-column'}
            alignContent={'flex-start'}
            className={index % 2 === 1 ? 'page-break' : ''}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex-column',
                  gap: 2,
                  flex: 1,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, width: '100%' }}>
                  <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>Goal: </Typography>
                  <Typography sx={{ textAlign: 'left' }}>{targetData.goalLabel}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, width: '100%' }}>
                  <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>Goal type:</Typography>
                  <Typography sx={{ textAlign: 'left' }}>
                    {goalType === GoalTypes.BehaviorReduction
                      ? 'Behavior reduction goal'
                      : goalType === GoalTypes.SkillAcquisition
                        ? 'Skill acquisition goal'
                        : 'Parent / Caregiver goal'}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex-column',
                  gap: 2,
                  flex: 1,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                  <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>Target:</Typography>
                  <Typography sx={{ textAlign: 'left' }}>{targetData.targetLabel}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1 }}>
                  <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
                    {targetData.averageLabel}:
                  </Typography>
                  <Typography sx={{ textAlign: 'left' }}>
                    {targetData.average}
                    {` ${
                      dcm === TPGoalDataCollectionMethod.PERCENTAGE
                        ? '%'
                        : dcm === TPGoalDataCollectionMethod.DURATION
                          ? durationUnitLabel
                          : dcm === TPGoalDataCollectionMethod.RATE
                            ? ''
                            : 'instances'
                    }`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <Typography fontSize={12} fontWeight={700}>{`(${getChartLabelByDCM(dcm)}${
                dcm === TPGoalDataCollectionMethod.DURATION
                  ? ` in ${durationDCMEventUnit.toLowerCase()}s`
                  : dcm === TPGoalDataCollectionMethod.RATE
                    ? ` per ${rateUnit.substring(0, rateUnit.length - 1)}`
                    : ''
              })`}</Typography>
            </Box>

            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                mt: 2,
                justifyContent: 'center',
              }}
            >
              <TargetChart targetData={targetData} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
});

export default PdfDocument;
